import { useId } from 'react';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { integrationService } from '../../services/integrations';
import type { RecipientsDraft } from './recipients-draft';

/**
 * Recipient builder shared by chains and recipient groups: email
 * addresses (comma-separated) + integration multi-select. Push (userId)
 * recipients arrive with the user-directory UI later — the API already
 * supports them. Draft helpers live in recipients-draft.ts (Fast
 * Refresh requires components-only modules).
 */

export function RecipientsEditor({
  draft,
  onChange,
}: {
  draft: RecipientsDraft;
  onChange: (next: RecipientsDraft) => void;
}) {
  const fieldId = useId();
  const integrationsQuery = usePickerOptions(['integrations'], (tid, startAfter) =>
    integrationService.list(tid, { limit: 100, startAfter })
  );
  return (
    <>
      <div className="form-field">
        <label htmlFor={`${fieldId}-emails`}>Email recipients (comma-separated)</label>
        <input
          id={`${fieldId}-emails`}
          name="emails"
          placeholder="noc@example.com, oncall@example.com"
          value={draft.emails}
          onChange={(e) => onChange({ ...draft, emails: e.target.value })}
        />
      </div>
      <div className="form-field">
        <label htmlFor={`${fieldId}-integrations`}>Integrations (Desk, Dispatch, webhooks)</label>
        <select
          id={`${fieldId}-integrations`}
          name="integrationIds"
          multiple
          size={4}
          value={draft.integrationIds}
          onChange={(e) =>
            onChange({
              ...draft,
              integrationIds: Array.from(e.target.selectedOptions).map(
                (o) => o.value
              ),
            })
          }
        >
          {(integrationsQuery.data?.items ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.kind})
            </option>
          ))}
        </select>
        {integrationsQuery.data?.truncated && (
          <p className="text-muted">Integration list truncated.</p>
        )}
      </div>
    </>
  );
}
